// src/pages/CrearVotacion.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, getDocs, query, orderBy, where, onSnapshot } from 'firebase/firestore';
import { FaSearch } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { registerMovement } from '../utils/registerMovement';
import { countEligibleVoters } from '../utils/countEligibleVoters';
import { obtenerNombreResidente } from '../utils/morosoUtils';
import './Votaciones.css';

const CATEGORIAS_VOTACION = [
    { id: 'Comunidad', label: 'Comunidad', icono: '👥' },
    { id: 'Vigilancia/Seguridad', label: 'Vigilancia / Seguridad', icono: '🛡️' },
    { id: 'Finanzas (Ingresos/Egresos)', label: 'Finanzas (Ingresos/Egresos)', icono: '💰' },
    { id: 'Administración', label: 'Administración', icono: '🏛️' }
];

const CrearVotacion = () => {
    const [titulo, setTitulo] = useState('');
    const [categoria, setCategoria] = useState('Comunidad');
    const [descripcion, setDescripcion] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [cargando, setCargando] = useState(false);
    const [metaParticipantes, setMetaParticipantes] = useState('');
    const [metaSugerida, setMetaSugerida] = useState(null);
    const [casas, setCasas] = useState([]);
    const [selectedCasas, setSelectedCasas] = useState(new Set());
    const [loadingCasas, setLoadingCasas] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mapResidentesUsuarios, setMapResidentesUsuarios] = useState({});
    const [tooltipData, setTooltipData] = useState({ visible: false, x: 0, y: 0, text: '', abajo: false });
    const navigate = useNavigate();
    const { votacionId } = useParams();
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();
    const isAdmin = useMemo(() => String(userData?.rol || '').toLowerCase() === 'superadmin', [userData]);
    const esEdicion = Boolean(votacionId);

    const handleCasaMouseEnter = (e, casa) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const num = casa.numero || casa.numeroCasa || 'S/N';
        const nombre = obtenerNombreResidente(casa) || mapResidentesUsuarios[casa.id] || mapResidentesUsuarios[String(casa.numero || casa.numeroCasa).trim()] || '';
        const texto = nombre ? `Casa ${num} · ${nombre}` : `Casa ${num} · Sin residente registrado`;
        const mostrarAbajo = rect.top < 65;
        setTooltipData({
            visible: true,
            x: rect.left + rect.width / 2,
            y: mostrarAbajo ? rect.bottom + 8 : rect.top - 8,
            text: texto,
            abajo: mostrarAbajo
        });
    };

    const handleCasaMouseLeave = () => {
        setTooltipData(prev => ({ ...prev, visible: false }));
    };

    useEffect(() => {
        if (userData && !isAdmin) {
            showNotification('No tienes permisos para gestionar votaciones.', 'error');
            navigate('/comunidad/votaciones');
        }
    }, [userData, isAdmin, showNotification, navigate]);

    useEffect(() => {
        const cargarVotacion = async () => {
            if (!esEdicion || !userData?.fraccionamientoId) return;
            setCargando(true);
            try {
                const ref = doc(db, 'fraccionamientos', userData.fraccionamientoId, 'votaciones', votacionId);
                const snap = await getDoc(ref);
                if (!snap.exists()) {
                    showNotification('No se encontró la votación solicitada.', 'error');
                    navigate('/comunidad/votaciones');
                    return;
                }
                const data = snap.data();
                setTitulo(data.titulo || '');
                setCategoria(data.categoria || 'Comunidad');
                setDescripcion(data.descripcion || '');
                setFechaFin(formatearFechaInput(data.fechaFin));
                if (data.metaParticipantes) {
                    setMetaParticipantes(String(data.metaParticipantes));
                }
                if (Array.isArray(data.casasHabilitadas)) {
                    setSelectedCasas(new Set(data.casasHabilitadas));
                }
            } catch (error) {
                console.error('Error al cargar la votación:', error);
                showNotification('No se pudo cargar la votación.', 'error');
                navigate('/comunidad/votaciones');
            } finally {
                setCargando(false);
            }
        };

        cargarVotacion();
    }, [esEdicion, votacionId, userData, showNotification, navigate]);

    // Cargar casas del fraccionamiento
    useEffect(() => {
        const cargarCasas = async () => {
            if (!userData?.fraccionamientoId) return;
            setLoadingCasas(true);
            try {
                const casasRef = collection(db, 'fraccionamientos', userData.fraccionamientoId, 'casas');
                const snap = await getDocs(query(casasRef, orderBy('numero', 'asc')));
                const listaRaw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                // Intentar ordenar numéricamente si es posible
                const listaOrdenada = listaRaw.sort((a, b) => {
                    const numA = parseInt(a.numero || 0);
                    const numB = parseInt(b.numero || 0);
                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                    return String(a.numero).localeCompare(String(b.numero));
                });
                
                setCasas(listaOrdenada);
            } catch (error) {
                console.error('CrearVotacion: error al cargar casas', error);
            } finally {
                setLoadingCasas(false);
            }
        };

        cargarCasas();
    }, [userData?.fraccionamientoId]);

    // Cargar nombres de residentes registrados en usuarios
    useEffect(() => {
        if (!userData?.fraccionamientoId) return;
        const qUsers = query(
            collection(db, 'usuarios'),
            where('fraccionamientoId', '==', userData.fraccionamientoId)
        );
        const unsubUsers = onSnapshot(qUsers, (snap) => {
            const mapping = {};
            snap.docs.forEach(d => {
                const u = d.data() || {};
                const name = (u.nombreCompleto || u.nombre || `${u.nombres || ''} ${u.apellidos || ''}`).trim();
                if (name) {
                    if (u.casaId) mapping[u.casaId] = name;
                    if (u.numeroCasa) mapping[String(u.numeroCasa).trim()] = name;
                }
            });
            setMapResidentesUsuarios(mapping);
        }, (err) => console.warn('CrearVotacion: error cargando usuarios', err));

        return () => unsubUsers();
    }, [userData?.fraccionamientoId]);

    const filteredCasas = useMemo(() => {
        if (!searchTerm.trim()) return casas;
        const q = searchTerm.toLowerCase().trim();
        return casas.filter(c => {
            const num = String(c.numero || c.numeroCasa || c.id || '').toLowerCase();
            const calle = String(c.calle || '').toLowerCase();
            const nombre = (obtenerNombreResidente(c) || mapResidentesUsuarios[c.id] || mapResidentesUsuarios[String(c.numero || c.numeroCasa).trim()] || '').toLowerCase();
            return num.includes(q) || calle.includes(q) || nombre.includes(q) || `casa ${num}`.includes(q);
        });
    }, [casas, searchTerm, mapResidentesUsuarios]);

    useEffect(() => {
        const recomendarMeta = async () => {
            if (!userData?.fraccionamientoId) {
                return;
            }

            try {
                const total = await countEligibleVoters(userData.fraccionamientoId);
                if (Number.isFinite(total) && total > 0) {
                    setMetaSugerida(total);
                    if (!esEdicion) {
                        setMetaParticipantes(String(total));
                    }
                }
            } catch (error) {
                console.error('CrearVotacion: no se pudo calcular meta sugerida', error);
            }
        };

        recomendarMeta();
    }, [userData, esEdicion]);
    
    const handleGuardarVotacion = async () => {
        if (!titulo.trim() || !descripcion.trim()) {
            showNotification('Por favor, completa todos los campos.', 'error');
            return;
        }
        if (!fechaFin) {
            showNotification('Por favor, selecciona la fecha de vencimiento de la votación.', 'error');
            return;
        }

        // Interpretar fechaFin (YYYY-MM-DD o datetime string) como 23:59:59.999 del día seleccionado
        let fin;
        if (/^\d{4}-\d{2}-\d{2}$/.test(fechaFin)) {
            const parts = fechaFin.split('-');
            fin = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 23, 59, 59, 999);
        } else {
            fin = new Date(fechaFin);
            if (fin.getHours() === 0 && fin.getMinutes() === 0) {
                fin.setHours(23, 59, 59, 999);
            }
        }
        if (isNaN(fin.getTime())) {
            showNotification('Fecha de fin inválida.', 'error');
            return;
        }

        if (fin < new Date()) {
            showNotification('La fecha de vencimiento no puede ser en el pasado. Selecciona hoy o una fecha futura.', 'error');
            return;
        }

        try {
            setCargando(true);
            const votacionesRef = collection(db, 'fraccionamientos', userData.fraccionamientoId, 'votaciones');
            const metaValida = selectedCasas.size > 0 ? selectedCasas.size : (Number.isFinite(Number(metaParticipantes)) ? Math.round(Number(metaParticipantes)) : null);
            const casasHabilitadas = Array.from(selectedCasas);
            const numerosCasasHabilitadas = casas
                .filter(c => selectedCasas.has(c.id))
                .map(c => String(c.numero || '').trim())
                .filter(Boolean);

            if (esEdicion) {
                const ref = doc(votacionesRef, votacionId);
                const nuevoEstado = fin <= new Date() ? 'vencida' : 'activa';
                await updateDoc(ref, {
                    titulo,
                    categoria: categoria || 'Comunidad',
                    descripcion,
                    fechaFin: fin,
                    estado: nuevoEstado,
                    metaParticipantes: metaValida,
                    casasHabilitadas: casasHabilitadas.length > 0 ? casasHabilitadas : null,
                    numerosCasasHabilitadas: numerosCasasHabilitadas.length > 0 ? numerosCasasHabilitadas : null,
                });
                showNotification('Votación actualizada con éxito.', 'success');
                try {
                    await registerMovement({
                        fraccionamientoId: userData.fraccionamientoId,
                        titulo: `Votación actualizada: ${titulo}`,
                        descripcion: `Categoría: ${categoria} · ${descripcion}`,
                        categoria: 'Votaciones',
                        tipoMovimiento: 'votacion_actualizada',
                        origen: 'votaciones',
                        metadata: {
                            votacionId,
                            categoria,
                            estado: nuevoEstado,
                            fechaFin: fin,
                        },
                        userData,
                        currentUser,
                    });
                } catch (logError) {
                    console.error('CrearVotacion: error al registrar movimiento de actualización', logError);
                }
            } else {
                const docRef = await addDoc(votacionesRef, {
                    titulo,
                    categoria: categoria || 'Comunidad',
                    descripcion,
                    estado: 'activa',
                    fechaCreacion: serverTimestamp(), // fecha de inicio automática
                    fechaFin: fin,
                    metaParticipantes: metaValida,
                    casasHabilitadas: casasHabilitadas.length > 0 ? casasHabilitadas : null,
                    numerosCasasHabilitadas: numerosCasasHabilitadas.length > 0 ? numerosCasasHabilitadas : null,
                    opciones: {
                        aFavor: 0,
                        enContra: 0
                    }
                });
                showNotification('Votación creada con éxito.', 'success');
                try {
                    await registerMovement({
                        fraccionamientoId: userData.fraccionamientoId,
                        titulo: `Nueva votación: ${titulo}`,
                        descripcion: `Categoría: ${categoria} · ${descripcion}`,
                        categoria: 'Votaciones',
                        tipoMovimiento: 'votacion_creada',
                        origen: 'votaciones',
                        metadata: {
                            votacionId: docRef.id,
                            categoria,
                            fechaFin: fin,
                        },
                        userData,
                        currentUser,
                    });
                } catch (logError) {
                    console.error('CrearVotacion: error al registrar movimiento de creación', logError);
                }
            }
            navigate('/comunidad/votaciones');
        } catch (error) {
            console.error("Error al crear la votación: ", error);
            showNotification('Hubo un error al guardar la votación.', 'error');
        } finally {
            setCargando(false);
        }
    };

    return (
        <div className="crear-votacion-page">
            <div className="form-container">
                <div className="form-header">
                    <button onClick={() => navigate('/comunidad/votaciones')} className="btn-outline retorno-link">← Volver</button>
                    <h2>{esEdicion ? 'Editar Votación' : 'Crear Nueva Votación'}</h2>
                </div>
                {cargando && <p className="muted">Cargando información...</p>}
                <div className="form-group">
                    <label>Título de la Votación</label>
                    <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ej. Aprobación de mejoras en área verde común" />
                </div>
                <div className="form-group">
                    <label>Categoría</label>
                    <div className="votacion-categoria-selector">
                        {CATEGORIAS_VOTACION.map(cat => (
                            <button
                                key={cat.id}
                                type="button"
                                className={`votacion-cat-option ${categoria === cat.id ? 'active' : ''}`}
                                onClick={() => setCategoria(cat.id)}
                            >
                                <span className="cat-icon">{cat.icono}</span>
                                <span className="cat-text">{cat.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="form-group">
                    <label>Descripción / Propuesta</label>
                    <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows="6"></textarea>
                </div>
                <div className="form-group">
                    <label>Fecha de vencimiento (día)</label>
                    <input
                        type="date"
                        value={fechaFin}
                        min={new Date().toLocaleDateString('en-CA')}
                        onChange={e => setFechaFin(e.target.value)}
                    />
                    <small className="muted">La votación permanecerá activa hasta las 23:59:59 hrs del día seleccionado.</small>
                </div>
                <div className="form-group">
                    <label>Participantes Habilitados (Domicilios)</label>
                    <div className="zen-address-selector">
                        <div className="zen-grid-header">
                            <div style={{ flex: 1 }}>
                                <h4 style={{ margin: 0, fontSize: '15px' }}>Selecciona los domicilios</h4>
                                <div className="zen-search-wrapper" style={{ marginTop: '12px', marginRight: '20px' }}>
                                    <FaSearch className="zen-search-icon" />
                                    <input 
                                        type="text" 
                                        placeholder="Buscar por número o residente..." 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="zen-search-input"
                                    />
                                </div>
                            </div>
                            <div className="zen-grid-controls">
                                <button className="btn-zen-action" onClick={() => {
                                    const all = new Set(casas.map(c => c.id));
                                    setSelectedCasas(all);
                                    setMetaParticipantes(String(all.size));
                                }}>Todos</button>
                                <button className="btn-zen-action" onClick={() => {
                                    setSelectedCasas(new Set());
                                    setMetaParticipantes('0');
                                }}>Ninguno</button>
                            </div>
                        </div>

                        {loadingCasas ? (
                            <p className="muted">Cargando domicilios...</p>
                        ) : (
                            <div className="zen-house-grid" onScroll={handleCasaMouseLeave}>
                                {filteredCasas.map(casa => {
                                    const isSelected = selectedCasas.has(casa.id);
                                    const num = casa.numero || casa.numeroCasa || 'S/N';
                                    return (
                                        <div 
                                            key={casa.id} 
                                            className={`zen-house-item ${isSelected ? 'selected' : ''}`}
                                            onMouseEnter={(e) => handleCasaMouseEnter(e, casa)}
                                            onMouseLeave={handleCasaMouseLeave}
                                            onClick={() => {
                                                const newSelection = new Set(selectedCasas);
                                                if (isSelected) newSelection.delete(casa.id);
                                                else newSelection.add(casa.id);
                                                setSelectedCasas(newSelection);
                                                setMetaParticipantes(String(newSelection.size));
                                            }}
                                        >
                                            {num}
                                            <div className="house-check">✓</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="zen-summary">
                            <div className="zen-badge">
                                {selectedCasas.size} domicilios seleccionados
                            </div>
                            <div style={{ fontSize: '14px', color: '#64748b' }}>
                                Total sugerido: <strong>{metaSugerida || casas.length}</strong>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="form-actions">
                    <button className="btn-secondary" onClick={() => navigate('/comunidad/votaciones')}>Cancelar</button>
                    <button className="btn-primary" onClick={handleGuardarVotacion} disabled={!fechaFin || cargando}>
                        {esEdicion ? 'Guardar Cambios' : 'Crear Votación'}
                    </button>
                </div>
            </div>

            {tooltipData.visible && (
                <div
                    className={`zen-fixed-tooltip ${tooltipData.abajo ? 'abajo' : 'arriba'}`}
                    style={{
                        left: `${tooltipData.x}px`,
                        top: `${tooltipData.y}px`
                    }}
                >
                    <span>👤 {tooltipData.text}</span>
                </div>
            )}
        </div>
    );
};

export default CrearVotacion;

function formatearFechaInput(valor) {
    if (!valor) return '';
    const date = typeof valor.toDate === 'function' ? valor.toDate() : (valor instanceof Date ? valor : new Date(valor));
    if (isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
}